import { useEffect, useState } from 'react'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { PageHeader } from '../../../components/PageHeader'
import { useAuth } from '../../../lib/auth'
import {
  deleteGameNote, localToday, saveTodayNote, useGameNotes,
} from '../../../lib/api/gameNotes'
import { getPpgGame, ppgNoteKey } from '../data/games'
import { useT } from '../../../i18n'

interface Props {
  gameNumber: number
  onBack: () => void
}

const PPG_RED = '#EF4B03'
const JDG_YELLOW = '#D9E026'   // brand lime — watermark for Jordan's own designs

/**
 * Two-step note deletion: a quiet trash can arms it, then a red check
 * with a "?" asks "really?" — tapping the check deletes. Arming decays
 * after a few seconds (handled by the parent) so a stray tap never
 * leaves a live delete button waiting.
 */
function DeleteNoteButton({ armed, onArm, onConfirm }: {
  armed: boolean
  onArm: () => void
  onConfirm: () => void
}) {
  const t = useT()
  if (armed) {
    return (
      <button
        type="button"
        onClick={onConfirm}
        aria-label={t('Confirm delete')}
        className="flex items-center gap-1 px-1 active:scale-90 transition-transform"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M20 6L9 17l-5-5" stroke="#C62828" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-sm font-bold text-brand-danger">?</span>
      </button>
    )
  }
  return (
    <button
      type="button"
      onClick={onArm}
      aria-label={t('Delete note')}
      className="px-1 active:scale-90 transition-transform"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m3 0-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6m5 5v6m4-6v6"
              stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  )
}

/** '2026-07-16' → 'JUL 16' (year appended only when it isn't this year). */
function pinLabel(noteDate: string): string {
  const [y, m, d] = noteDate.split('-').map(Number)
  const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  const label = `${MONTHS[(m ?? 1) - 1]} ${d}`
  return y === new Date().getFullYear() ? label : `${label} ${y}`
}

/**
 * One Portable Party game: the full rules, read-only and app-native
 * (no PDF), followed by the NOTES section from the printed book made
 * functional — one pinned note per day, saved to the account.
 */
export function PortablePartyGameScreen({ gameNumber, onBack }: Props) {
  const t = useT()
  const game = getPpgGame(gameNumber)
  const { session, exitGuestMode } = useAuth()
  // Anonymous "guest" sessions have no profiles row, so notes can't
  // save for them — treat them like signed-out (same rule as App.tsx's
  // `signedIn`).
  const isAnon = Boolean((session?.user as { is_anonymous?: boolean } | undefined)?.is_anonymous)
  const realUser = Boolean(session) && !isAnon
  const noteKey = ppgNoteKey(gameNumber)
  const { notes, loading, refresh } = useGameNotes(noteKey)

  const today = localToday()
  const todaysNote = notes.find(n => n.note_date === today)
  const pinned = notes.filter(n => n.note_date !== today)

  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveState, setSaveState] = useState<'idle' | 'saved' | 'error'>('idle')
  // note_date of the note whose delete is armed (trash tapped once).
  const [armedDelete, setArmedDelete] = useState<string | null>(null)

  // Prefill today's draft once notes arrive (and again if refresh
  // brings a newer body, e.g. saved from another device today).
  useEffect(() => { setDraft(todaysNote?.body ?? '') }, [todaysNote?.body])

  // An armed delete disarms itself after a few seconds untouched.
  useEffect(() => {
    if (!armedDelete) return
    const t = setTimeout(() => setArmedDelete(null), 4000)
    return () => clearTimeout(t)
  }, [armedDelete])

  if (!game || game.title === null) {
    // Defensive: list screen never links here for empty slots.
    return (
      <div className="screen">
        <PageHeader title={t('Coming soon')} onBack={onBack} />
      </div>
    )
  }

  const save = async () => {
    const body = draft.trim()
    if (!body || saving) return
    setSaving(true)
    setSaveState('idle')
    try {
      await saveTodayNote(noteKey, body)
      setSaveState('saved')
      refresh()
    } catch {
      setSaveState('error')
    } finally {
      setSaving(false)
    }
  }

  const removeNote = async (noteDate: string) => {
    setArmedDelete(null)
    try {
      await deleteGameNote(noteKey, noteDate)
      refresh()
    } catch { /* non-fatal: the list simply keeps the note */ }
  }

  // Jordan Draper originals swap the PPG green for the brand yellow
  // across the whole page (title, headings, chips, watermark); their
  // origin chip reads "Jordan Draper" straight from the data.
  const accentText = game.jdg ? 'text-brand-lime-dark' : 'text-game-portable-party-dark'
  const chipClass = game.jdg
    ? 'bg-brand-lime/20 text-brand-lime-dark border-brand-lime-dark/40'
    : 'bg-game-portable-party-light text-game-portable-party-dark border-game-portable-party-dark/30'

  return (
    <div className="screen">
      <BackgroundTitle text={game.title} color={game.jdg ? JDG_YELLOW : PPG_RED} />
      <PageHeader
        title={game.title}
        subtitle={t('Portable Party Games · No. {n}', { n: String(game.number).padStart(2, '0') })}
        onBack={onBack}
      />

      <div className="flex-1 overflow-y-auto px-5 pb-10 relative z-10">
        {/* Meta strip — the book's footer: ORIGIN · BRITAIN // PLAYERS · 4+ … */}
        {game.meta && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {[
              ['ORIGIN', game.meta.origin],
              ['PLAYERS', game.meta.players],
              ['TIME', game.meta.time],
              ['AGES', game.meta.ages],
            ].map(([label, value]) => (
              <span key={label}
                className={`text-[11px] font-display font-bold uppercase tracking-wide
                            border rounded-full px-2.5 py-1 ${chipClass}`}>
                {label} · {value}
              </span>
            ))}
          </div>
        )}

        {/* Rules — read-only, straight from the book. */}
        <div className="card p-4 mb-4">
          {game.sections?.map(section => (
            <section key={section.heading} className="mb-4 last:mb-0">
              <h2 className={`font-display font-black uppercase tracking-wide mb-1 ${accentText}`}>
                {section.heading}
              </h2>
              {/* pre-line: rule bodies carry \n for example chains
                  (t('Tiger → Rhinoceros')) straight from the book text. */}
              <p className="text-sm text-brand-text leading-relaxed whitespace-pre-line wrap-break-word">{section.body}</p>
            </section>
          ))}

          {game.variations && game.variations.length > 0 && (
            <section className="mt-4 pt-4 border-t border-brand-border/40">
              <h2 className={`font-display font-black uppercase tracking-wide mb-2 ${accentText}`}>
                Variations
              </h2>
              {game.variations.map(v => (
                <p key={v.name} className="text-sm text-brand-text leading-relaxed mb-2 last:mb-0">
                  <span className="font-semibold">{v.name}:</span> {v.body}
                </p>
              ))}
            </section>
          )}
        </div>

        {/* NOTES — the printed book ends each game with a NOTES box;
            here it saves. One note per day, pinned with its date. */}
        <h2 className="font-display font-black uppercase tracking-wide text-brand-text mb-2">
          Notes
        </h2>

        {!realUser ? (
          <div className="card p-4 mb-4">
            <p className="text-sm text-brand-muted mb-3">{t('Notes save to your account and follow you across devices. Sign in to start keeping them.')}</p>
            <button type="button" onClick={exitGuestMode} className="btn-primary w-full">{t('Sign in')}</button>
          </div>
        ) : (
          <>
            <div className="card p-4 mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-display font-bold uppercase tracking-wide
                                 bg-brand-lime/30 text-brand-text rounded-full px-2.5 py-1">
                  {t('{date} · Today', { date: pinLabel(today) })}
                </span>
                <div className="flex items-center gap-2">
                  {saveState === 'saved' && (
                    <span className="text-xs text-brand-success font-medium">{t('Saved')}</span>
                  )}
                  {saveState === 'error' && (
                    <span className="text-xs text-brand-danger font-medium">{t('Could not save. Try again')}</span>
                  )}
                  {todaysNote && (
                    <DeleteNoteButton
                      armed={armedDelete === today}
                      onArm={() => setArmedDelete(today)}
                      onConfirm={() => removeNote(today)}
                    />
                  )}
                </div>
              </div>
              <textarea
                value={draft}
                onChange={e => { setDraft(e.target.value); setSaveState('idle') }}
                placeholder={t('House rules, best hiding spots, who giggled first…')}
                maxLength={2000}
                rows={3}
                className="w-full text-sm text-brand-text bg-brand-elevated rounded-xl
                           border border-brand-border p-3 resize-none
                           focus:outline-hidden focus:border-brand-lime-dark"
              />
              <button
                type="button"
                onClick={save}
                disabled={saving || !draft.trim() || draft.trim() === (todaysNote?.body ?? '')}
                className="btn-primary w-full mt-2 disabled:opacity-40"
              >
                {saving ? 'Saving…' : todaysNote ? t('Update today’s note') : t('Save note')}
              </button>
            </div>

            {loading && notes.length === 0 && (
              <p className="text-xs text-brand-muted">{t('Loading notes…')}</p>
            )}

            {pinned.map(note => (
              <div key={note.note_date} className="card p-4 mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-display font-bold uppercase tracking-wide
                                   bg-brand-elevated text-brand-muted border border-brand-border
                                   rounded-full px-2.5 py-1">
                    {pinLabel(note.note_date)}
                  </span>
                  <DeleteNoteButton
                    armed={armedDelete === note.note_date}
                    onArm={() => setArmedDelete(note.note_date)}
                    onConfirm={() => removeNote(note.note_date)}
                  />
                </div>
                <p className="text-sm text-brand-text leading-relaxed whitespace-pre-wrap wrap-break-word">
                  {note.body}
                </p>
              </div>
            ))}

            {!loading && pinned.length === 0 && !todaysNote && (
              <p className="text-xs text-brand-muted">{t('Your first note pins here with today’s date. Each day gets its own.')}</p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
